import {
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import { ComponentType, ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Select, { OptionProps, SingleValueProps } from 'react-select';
import { z } from 'zod';
import { bookmarkSchema } from '~components/displays/useManageWebPagesForm';
import MultiSelectPills from '~components/MultiSelectPills';
import { HighlightDaysAndTimes } from '~components/powerSchedules/PowerScheduleDetail/HighlightDaysAndTimes';
import { TimeSelect } from '~components/powerSchedules/TimeSelect';
import { ErrorAlert, WarningAlert } from '~components/ui/Alert';
import { IconProps } from '~components/ui/icons';
import { BrightnessIcon } from '~components/ui/icons/BrightnessIcon';
import { components as selectComponents } from '~components/ui/Select';
import { Slider } from '~components/ui/Slider';
import {
  Day,
  ScheduleTimeBlockInput,
  TimeBlockCategory,
  TimeBlockType,
} from '~graphql/__generated__/types';
import { DAYS_OF_WEEK_MON_FIRST, HOUR_REGEX } from '~utils/timeBlocks';
import {
  assignTimeBlockPriorities,
  DEFAULT_INPUT_SOURCE_VALUE,
  endAfterStart,
  getTimeBlockCategory,
  ScheduleValidationError,
  timeBlockTypesForCategory,
  validateAndSortScheduleTimeBlocks,
} from './scheduleTimeBlocks';
import {
  CATEGORY_META,
  TIME_BLOCK_CONTENT_COLOR,
  TIME_BLOCK_DATA_LABEL,
  TIME_BLOCK_DATA_REQUIRED,
  TIME_BLOCK_META,
} from './timeBlockMeta';

export type PlaylistOption = { id: string; title: string; mediaType: string };
/** `applicationId` is what the device understands; the subscription id is not. */
export type AppOption = { applicationId: string; name: string };

export const WHOLE_DAY_START = '00:00';
export const WHOLE_DAY_END = '23:59';
export const DEFAULT_BRIGHTNESS = 80;

/** '00:00' is also treated as an end of day, so slots saved that way still read as whole-day. */
const isWholeDayBlock = (start: string, end: string) =>
  start === WHOLE_DAY_START && (end === WHOLE_DAY_END || end === '00:00');

const FormData = z.object({
  start: z.string().regex(HOUR_REGEX),
  end: z.string().regex(HOUR_REGEX),
  isWholeDay: z.boolean(),
  days: z.array(z.nativeEnum(Day)),
  category: z.nativeEnum(TimeBlockCategory),
  type: z.nativeEnum(TimeBlockType),
  playlistId: z.string().optional(),
  applicationId: z.string().optional(),
  url: bookmarkSchema.optional(),
  source: z.string().optional(),
  brightness: z.number().int().min(0).max(100).optional(),
  isRemoved: z.boolean(),
});
type FormData = z.infer<typeof FormData>;

/** What `watch` hands the subscriber: every field optional, days possibly sparse. */
type WatchedValues = Partial<Omit<FormData, 'days'>> & { days?: Array<Day | undefined> };

const areBlocksEqual = (a?: ScheduleTimeBlockInput, b?: ScheduleTimeBlockInput) => {
  if ((!a && b) || (a && !b)) return false;
  return (
    a?.day === b?.day &&
    a?.start === b?.start &&
    a?.end === b?.end &&
    a?.type === b?.type &&
    a?.priority === b?.priority &&
    isEqual(a?.data, b?.data)
  );
};

const buildData = (type: TimeBlockType, values: WatchedValues, playlists: PlaylistOption[]) => {
  switch (type) {
    case TimeBlockType.Playlist: {
      const playlist = playlists.find((p) => p.id === values.playlistId);
      return { id: values.playlistId, mediaType: playlist?.mediaType };
    }
    case TimeBlockType.Apps:
      return { applicationId: values.applicationId };
    case TimeBlockType.Bookmark:
      return { url: values.url };
    case TimeBlockType.Source:
      return { source: values.source };
    case TimeBlockType.DefaultInputSource:
      return { source: DEFAULT_INPUT_SOURCE_VALUE };
    case TimeBlockType.Brightness:
      return { value: values.brightness };
    default:
      return {};
  }
};

/**
 * False while the payload for the selected type is still empty, so the form stays
 * disabled without surfacing an error the user cannot act on yet.
 */
const isDataComplete = (type: TimeBlockType, values: WatchedValues) => {
  switch (type) {
    case TimeBlockType.Playlist:
      return !!values.playlistId;
    case TimeBlockType.Apps:
      return !!values.applicationId?.trim();
    case TimeBlockType.Bookmark:
      return !!values.url?.trim() && bookmarkSchema.safeParse(values.url).success;
    case TimeBlockType.Source:
      return !!values.source?.trim();
    case TimeBlockType.DefaultInputSource:
      return true;
    case TimeBlockType.Brightness:
      return values.brightness != null;
    default:
      return false;
  }
};

export type IconOption = {
  value: string;
  label: string;
  icon?: ComponentType<IconProps>;
  iconColor?: string;
  iconBackground?: string;
};

/** The rounded icon badge that fronts an option, as drawn in the design. */
function OptionBadge({ option }: { option: IconOption }) {
  if (!option.icon) return null;

  const BadgeIcon = option.icon;

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      padding="1"
      marginRight="3"
      borderRadius="base"
      background={option.iconBackground}
    >
      <BadgeIcon width={6} height={6} color={option.iconColor} />
    </Flex>
  );
}

function IconOptionRow({ children, isSelected, innerProps, data }: OptionProps<IconOption>) {
  return (
    <Flex
      alignItems="center"
      paddingX="3"
      paddingY="2"
      cursor="pointer"
      color={isSelected ? 'white' : 'blue.800'}
      background={isSelected ? 'blue.400' : 'white'}
      fontSize="md"
      _hover={{ background: isSelected ? 'blue.400' : 'blue.50' }}
      {...innerProps}
    >
      <OptionBadge option={data} />
      {children}
    </Flex>
  );
}

function IconSingleValue({ children, data }: SingleValueProps<IconOption>) {
  return (
    <Flex alignItems="center" fontSize="md">
      <OptionBadge option={data} />
      {children}
    </Flex>
  );
}

const iconSelectComponents = {
  ...selectComponents,
  Option: IconOptionRow,
  SingleValue: IconSingleValue,
};

/**
 * A dropdown built on the project's react-select wrapper. Options may carry an icon
 * badge, which a native <select> cannot render; only "Schedule for" and "Schedule
 * type" use it - the payload dropdowns are plain.
 */
export function IconSelect({
  value,
  options,
  onChange,
  isDisabled,
  placeholder,
  isSearchable = false,
}: {
  value?: string;
  options: IconOption[];
  onChange: (value: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  isSearchable?: boolean;
}) {
  return (
    <Select<IconOption>
      value={options.find((option) => option.value === value) ?? null}
      options={options}
      onChange={(option) => option && onChange(option.value)}
      components={iconSelectComponents}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      isClearable={false}
      placeholder={placeholder ?? t('select')}
      menuPlacement="auto"
      menuPosition="fixed"
    />
  );
}

export function useEditScheduleTimeBlocks({
  timeBlocks: existingTimeBlocks,
  defaultCategory,
  defaultType,
  playlists,
  inputSources,
  apps,
  defaultValues,
  currentTimeBlock,
  onSubmit,
  onCancel,
  allowSelectDays,
  useShortErrorMessage = false,
}: {
  timeBlocks: ScheduleTimeBlockInput[];
  defaultCategory: TimeBlockCategory;
  /** Preselects the schedule type, e.g. when the dialog is opened from a legend tag. */
  defaultType?: TimeBlockType;
  playlists: PlaylistOption[];
  inputSources: string[];
  apps: AppOption[];
  defaultValues?: { start: string; end: string; days: Day[] };
  currentTimeBlock?: ScheduleTimeBlockInput;
  onSubmit: (timeBlocks: ScheduleTimeBlockInput[]) => void;
  onCancel: () => void;
  allowSelectDays: boolean;
  useShortErrorMessage?: boolean;
}) {
  const [formDisabled, setFormDisabled] = useState<{
    isDisabled: boolean;
    reason: string | undefined;
    title?: string;
    severity?: 'error' | 'warning';
  }>({ isDisabled: false, reason: undefined });

  const [timeBlocks, setTimeBlocks] = useState(existingTimeBlocks);

  // Editing keeps the block's own priority: the dialog does not expose priority.
  const priority = currentTimeBlock?.priority ?? 0;

  const newDefaultValues: FormData = currentTimeBlock
    ? {
        start: currentTimeBlock.start,
        end: currentTimeBlock.end,
        isWholeDay: isWholeDayBlock(currentTimeBlock.start, currentTimeBlock.end),
        days: [currentTimeBlock.day],
        category: getTimeBlockCategory(currentTimeBlock.type),
        type: currentTimeBlock.type,
        playlistId: currentTimeBlock.data.id ?? undefined,
        applicationId: currentTimeBlock.data.applicationId ?? undefined,
        url: currentTimeBlock.data.url ?? undefined,
        source: currentTimeBlock.data.source ?? undefined,
        brightness: currentTimeBlock.data.value ?? DEFAULT_BRIGHTNESS,
        isRemoved: false,
      }
    : {
        start: defaultValues?.start ?? '09:00',
        end: defaultValues?.end ?? '10:00',
        isWholeDay: false,
        days: defaultValues?.days ?? [],
        category: defaultCategory,
        type: defaultType ?? timeBlockTypesForCategory(defaultCategory)[0],
        brightness: DEFAULT_BRIGHTNESS,
        isRemoved: false,
      };

  const {
    register,
    reset,
    watch,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: newDefaultValues,
    mode: 'onChange',
    resolver: zodResolver(FormData),
  });

  const currentValues: FormData = {
    isRemoved: watch('isRemoved', newDefaultValues.isRemoved),
    start: watch('start', newDefaultValues.start),
    end: watch('end', newDefaultValues.end),
    isWholeDay: watch('isWholeDay', newDefaultValues.isWholeDay),
    days: watch('days', newDefaultValues.days),
    category: watch('category', newDefaultValues.category),
    type: watch('type', newDefaultValues.type),
    playlistId: watch('playlistId', newDefaultValues.playlistId),
    applicationId: watch('applicationId', newDefaultValues.applicationId),
    url: watch('url', newDefaultValues.url),
    source: watch('source', newDefaultValues.source),
    brightness: watch('brightness', newDefaultValues.brightness),
  };

  // Mirrors the power schedule form: react-hook-form's `isDirty` does not fire for
  // inputs that are set through `setValue` rather than registered.
  const isDirty = !isEqual(currentValues, newDefaultValues);

  const updateTimeValue = (property: 'start' | 'end', value: string) => {
    setValue(property, value);

    const start = property === 'start' ? value : currentValues.start;
    const end = property === 'end' ? value : currentValues.end;
    if (property === 'start' && HOUR_REGEX.test(start) && end !== '00:00' && start >= end) {
      updateTimeValue('end', endAfterStart(start));
    }
  };

  const handleWholeDayChange = (isWholeDay: boolean) => {
    setValue('isWholeDay', isWholeDay);
    if (isWholeDay) {
      setValue('start', WHOLE_DAY_START);
      setValue('end', WHOLE_DAY_END);
    }
  };

  // Switching track resets the type, since the two tracks share no block types.
  const handleCategoryChange = (category: TimeBlockCategory) => {
    setValue('category', category);
    setValue('type', timeBlockTypesForCategory(category)[0]);
  };

  useEffect(
    function onFormChange() {
      const validate = (values: WatchedValues) => {
        const { start, end, isRemoved, type } = values;
        const days = values.days?.filter((day): day is Day => !!day) ?? [];

        if (
          !start ||
          !end ||
          !HOUR_REGEX.test(start) ||
          !HOUR_REGEX.test(end) ||
          !type ||
          days.length !== values.days?.length ||
          !days.length
        ) {
          return setFormDisabled({ isDisabled: true, reason: undefined });
        }

        if (!isRemoved && !isDataComplete(type, values)) {
          return setFormDisabled({ isDisabled: true, reason: undefined });
        }

        try {
          const newTimeBlocks = (() => {
            if (isRemoved) {
              return existingTimeBlocks.filter((tb) => !areBlocksEqual(tb, currentTimeBlock));
            }

            const edited = {
              start,
              end,
              type,
              priority,
              data: buildData(type, values, playlists),
            };

            if (currentTimeBlock) {
              return existingTimeBlocks.map((tb) =>
                areBlocksEqual(tb, currentTimeBlock) ? { ...edited, day: days[0] } : tb,
              );
            }

            return [...existingTimeBlocks, ...days.map((day) => ({ ...edited, day }))];
          })();

          const { validatedTimeBlocks } = validateAndSortScheduleTimeBlocks(
            assignTimeBlockPriorities(newTimeBlocks),
            { useShortErrorMessage },
          );
          setTimeBlocks(validatedTimeBlocks);
          setFormDisabled({ isDisabled: false, reason: undefined });
        } catch (err) {
          if (err instanceof Error)
            setFormDisabled({
              isDisabled: true,
              reason: err.message,
              title: err instanceof ScheduleValidationError ? err.title : undefined,
              severity: err instanceof ScheduleValidationError ? err.severity : undefined,
            });
        }
      };

      // `watch` only fires on a change, so without this the form starts out
      // reported as valid and stays that way until the first edit - long enough to
      // submit a slot that has no days, or no payload for its type.
      validate(getValues());

      const subscription = watch(validate);

      return () => subscription.unsubscribe();
    },
    [
      currentTimeBlock,
      existingTimeBlocks,
      getValues,
      playlists,
      priority,
      useShortErrorMessage,
      watch,
    ],
  );

  const submit = handleSubmit(() => {
    onSubmit(timeBlocks);
    if (!currentValues.isRemoved) reset();
  });

  const DataField = (() => {
    const label = t(TIME_BLOCK_DATA_LABEL[currentValues.type]);
    const isRequired = TIME_BLOCK_DATA_REQUIRED[currentValues.type];

    switch (currentValues.type) {
      case TimeBlockType.Playlist:
        return (
          <FormControl isRequired={isRequired} isInvalid={Boolean(errors.playlistId)}>
            <FormLabel>{label}</FormLabel>
            <IconSelect
              value={currentValues.playlistId}
              options={playlists.map((playlist) => ({
                value: playlist.id,
                label: playlist.title,
              }))}
              onChange={(value) => setValue('playlistId', value)}
              isDisabled={currentValues.isRemoved}
              isSearchable
            />
            <FormErrorMessage>{errors.playlistId?.message}</FormErrorMessage>
          </FormControl>
        );
      case TimeBlockType.DefaultInputSource:
        return null;
      case TimeBlockType.Source:
        return (
          <FormControl isRequired={isRequired} isInvalid={Boolean(errors.source)}>
            <FormLabel>{label}</FormLabel>
            <IconSelect
              value={currentValues.source}
              options={inputSources.map((source) => ({
                value: source,
                label: source,
              }))}
              onChange={(value) => setValue('source', value)}
              isDisabled={currentValues.isRemoved}
              isSearchable
            />
            <FormErrorMessage>{errors.source?.message}</FormErrorMessage>
          </FormControl>
        );
      case TimeBlockType.Apps:
        return (
          <FormControl isRequired={isRequired} isInvalid={Boolean(errors.applicationId)}>
            <FormLabel>{label}</FormLabel>
            <IconSelect
              value={currentValues.applicationId}
              options={apps.map((app) => ({
                value: app.applicationId,
                label: app.name,
              }))}
              onChange={(value) => setValue('applicationId', value)}
              isDisabled={currentValues.isRemoved}
              isSearchable
            />
            <FormErrorMessage>{errors.applicationId?.message}</FormErrorMessage>
          </FormControl>
        );
      case TimeBlockType.Bookmark:
        return (
          <FormControl isRequired={isRequired} isInvalid={Boolean(errors.url)}>
            <FormLabel>{label}</FormLabel>
            <Input
              background="white"
              placeholder="https://webpage.com"
              isDisabled={currentValues.isRemoved}
              {...register('url')}
            />
            <FormErrorMessage>{errors.url?.message}</FormErrorMessage>
          </FormControl>
        );
      case TimeBlockType.Brightness:
        return (
          <FormControl isInvalid={Boolean(errors.brightness)}>
            <FormLabel>{label}</FormLabel>
            <Slider
              value={currentValues.brightness ?? DEFAULT_BRIGHTNESS}
              onChange={(value) => setValue('brightness', value)}
              leftIcon={BrightnessIcon}
              min={0}
              max={100}
              isDisabled={currentValues.isRemoved}
            />
            <FormErrorMessage>{errors.brightness?.message}</FormErrorMessage>
          </FormControl>
        );
      default:
        return null;
    }
  })();

  const FormBody = (
    <Box>
      <Flex alignItems="end" gap={4}>
        <FormControl isInvalid={Boolean(errors.start)} width="82px" flexShrink={0}>
          <FormLabel whiteSpace="nowrap">{t('startTime')}</FormLabel>
          <TimeSelect
            allowIncompleteInput
            value={currentValues.start}
            onChange={updateTimeValue}
            property="start"
            disabled={currentValues.isRemoved || currentValues.isWholeDay}
          />
          <FormErrorMessage>{errors.start?.message}</FormErrorMessage>
        </FormControl>
        <Box paddingBottom="2" fontWeight="semibold">
          -
        </Box>
        <FormControl isInvalid={Boolean(errors.end)} width="82px" flexShrink={0}>
          <FormLabel whiteSpace="nowrap">{t('endTime')}</FormLabel>
          <TimeSelect
            allowIncompleteInput
            value={currentValues.end}
            onChange={updateTimeValue}
            property="end"
            endsBeforeMidnight
            disabled={currentValues.isRemoved || currentValues.isWholeDay}
          />
          <FormErrorMessage>{errors.end?.message}</FormErrorMessage>
        </FormControl>
        {currentTimeBlock && (
          <Button
            variant="ghost"
            color="red.400"
            padding={2}
            marginLeft={4}
            isDisabled={currentValues.isRemoved}
            _disabled={{ background: 'red.50', opacity: 0.5, pointerEvents: 'none' }}
            onClick={() => setValue('isRemoved', true)}
          >
            {t('removeTimeSlot')}
          </Button>
        )}
      </Flex>

      <Checkbox
        marginTop={3}
        isChecked={currentValues.isWholeDay}
        isDisabled={currentValues.isRemoved}
        onChange={(event) => handleWholeDayChange(event.target.checked)}
      >
        {t('scheduleWholeDay')}
      </Checkbox>

      <FormControl marginTop={4} isInvalid={Boolean(errors.category)}>
        <FormLabel>{t('scheduleFor')}</FormLabel>
        <IconSelect
          value={currentValues.category}
          options={Object.values(TimeBlockCategory).map((category) => ({
            value: category,
            label: t(CATEGORY_META[category].label),
            icon: CATEGORY_META[category].icon,
            iconColor: TIME_BLOCK_CONTENT_COLOR,
            iconBackground: CATEGORY_META[category].badgeBackground,
          }))}
          onChange={(value) => handleCategoryChange(value as TimeBlockCategory)}
          isDisabled={currentValues.isRemoved}
        />
        <FormErrorMessage>{errors.category?.message}</FormErrorMessage>
      </FormControl>

      <FormControl marginTop={4} isRequired isInvalid={Boolean(errors.type)}>
        <FormLabel>{t('scheduleType')}</FormLabel>
        <IconSelect
          value={currentValues.type}
          options={timeBlockTypesForCategory(currentValues.category).map((type) => ({
            value: type,
            label: t(TIME_BLOCK_META[type].label),
            icon: TIME_BLOCK_META[type].blockIcon,
            iconColor: TIME_BLOCK_CONTENT_COLOR,
            iconBackground: TIME_BLOCK_META[type].badgeBackground,
          }))}
          onChange={(value) => setValue('type', value as TimeBlockType)}
          isDisabled={currentValues.isRemoved}
        />
        <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
      </FormControl>

      {DataField && <Box marginTop={4}>{DataField}</Box>}

      {allowSelectDays && (
        <FormControl marginTop={4} isInvalid={Boolean(errors.days)}>
          <FormLabel>{t('days')}</FormLabel>
          <MultiSelectPills
            options={DAYS_OF_WEEK_MON_FIRST.map((day) => ({
              value: day,
              label: capitalize(t(day).substring(0, 3)),
            }))}
            onChange={(values) => setValue('days', values as Day[])}
            inputProps={register('days')}
            value={currentValues.days}
          />
        </FormControl>
      )}

      <Collapse in={formDisabled.isDisabled && !!formDisabled.reason} animateOpacity>
        <Box
          marginTop={formDisabled.isDisabled ? (useShortErrorMessage ? 8 : 10) : 0}
          transition="margin 100ms"
        >
          {formDisabled.severity === 'warning' ? (
            <WarningAlert>{formDisabled.reason}</WarningAlert>
          ) : (
            <ErrorAlert>
              {formDisabled.title && <AlertTitle>{formDisabled.title}</AlertTitle>}
              <AlertDescription>
                <HighlightDaysAndTimes>{formDisabled.reason || ''}</HighlightDaysAndTimes>
              </AlertDescription>
            </ErrorAlert>
          )}
        </Box>
      </Collapse>
    </Box>
  );

  const SubmitButton = ({ children }: { children: ReactNode }) => (
    <Button
      variant="solid"
      colorScheme="blue"
      marginLeft="3"
      type="submit"
      isDisabled={
        !isDirty || isSubmitting || (currentValues.isRemoved ? false : formDisabled.isDisabled)
      }
      isLoading={isSubmitting}
    >
      {children}
    </Button>
  );

  const CancelButton = ({ children }: { children?: ReactNode }) => (
    <Button
      variant="ghost"
      colorScheme="blue"
      onClick={() => {
        if (currentValues.isRemoved) setTimeout(() => setValue('isRemoved', false), 500);
        onCancel();
        reset();
      }}
      isDisabled={isSubmitting}
    >
      {children || t('cancel')}
    </Button>
  );

  return {
    handleSubmit: submit,
    reset,
    currentValues,
    FormBody,
    SubmitButton,
    CancelButton,
  };
}
